import React, { Fragment, forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Drawer, { DrawerVariants, } from '@polestar/component-warehouse-react/components/drawer/Drawer';
import Alignments from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import { TextButton } from '@polestar/component-warehouse-react/components/button/Button';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import Icon, { IconSizes } from '../icon/Icon';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import Heading from '../heading/Heading';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import Accordion from '../accordion/Accordion';
import AccordionSection from '../accordion/AccordionSection';
import Link, { LinkVariants } from '@polestar/component-warehouse-react/components/link/Link';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { setCSSCustomProp, useCSSCustomPropWatch, } from '@polestar/component-warehouse-react/hooks/useCSSCustomProp';
import { cssCustomPropPrefix } from '@polestar/component-warehouse-react/helpers/style';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
const MarketSelector = forwardRef(({ activeMarketLabel, ariaLabelButton, drawerProps, open = undefined, onClose, onOpen, id, ...props }, ref) => {
    const { title, regions, global, marketCountPerColumn } = drawerProps || {};
    const [isDrawerOpen, setIsDrawerOpen] = useState(drawerProps.isOpen || open || false);
    const { name } = useMediaQuery();
    const isMobile = name === DeviceTypes.mobile;
    const internalMarketCountPerColumn = marketCountPerColumn || 7;
    const columnsPerRow = 3;
    const memoizedId = useSSRSafeId('market-selector', id);
    const onGlobalLinkClick = useCallback(() => {
        setIsDrawerOpen(false);
        onClose && onClose();
        global.onClick && global.onClick();
    }, [global, setIsDrawerOpen, onClose]);
    const onCloseDrawer = useCallback(() => {
        setCSSCustomProp(MarketSelectorCSSCustomProps.open, '0');
        setIsDrawerOpen(false);
        onClose && onClose();
    }, [onClose, setIsDrawerOpen]);
    const onOpenDrawer = useCallback(() => {
        setIsDrawerOpen(true);
        onOpen && onOpen();
    }, [onOpen, setIsDrawerOpen]);
    const onMarketLinkClick = useCallback((e, market) => {
        setIsDrawerOpen(false);
        market.onClick && market.onClick(e, market);
    }, [setIsDrawerOpen]);
    useEffect(() => {
        setIsDrawerOpen(open);
    }, [open]);
    const amountOfMarketPerGridColumn = internalMarketCountPerColumn;
    const gridColumns = useMemo(() => {
        const gridColumns = [];
        if (!isMobile && !!regions) {
            let gridColumn = { title: undefined, markets: [] };
            let foundTitle = undefined;
            regions.map((region) => {
                if (foundTitle !== region.title) {
                    gridColumn.title = region.title;
                    foundTitle = region.title;
                }
                region.markets.map((market, marketIndex) => {
                    if (gridColumn.markets.length === amountOfMarketPerGridColumn) {
                        // create new gridColumn while too many markets for 1 column
                        gridColumns.push(gridColumn);
                        gridColumn = { title: undefined, markets: [market] };
                        if (marketIndex >= internalMarketCountPerColumn * columnsPerRow) {
                            gridColumn = { title: region.title, markets: [market] };
                        }
                    }
                    else {
                        gridColumn.markets.push(market);
                    }
                });
                // create new gridColumn
                gridColumns.push(gridColumn);
                gridColumn = { title: undefined, markets: [] };
            });
        }
        return gridColumns;
    }, [regions, isMobile, amountOfMarketPerGridColumn, internalMarketCountPerColumn]);
    // Find the active market and set it to expanded in the format of 'accordion-region-section-n'
    const activeMarketIndex = useMemo(() => {
        let marketIndex = 0;
        regions.forEach((region, index) => {
            return region.markets.forEach((market) => {
                if (market.label === activeMarketLabel) {
                    marketIndex = index;
                }
            });
        });
        return `accordion-region-section-${marketIndex}`;
    }, [activeMarketLabel, regions]);
    const cssCustomProp = useCSSCustomPropWatch('div[data-version]', MarketSelectorCSSCustomProps.open);
    useEffect(() => {
        cssCustomProp.on('change', (val) => {
            // if the CSS Custom Property changed then update here
            if (val === '1' && !isDrawerOpen) {
                setIsDrawerOpen(true);
            }
            if (val === '0' && isDrawerOpen) {
                setIsDrawerOpen(false);
            }
        });
        return () => {
            cssCustomProp.destroy();
        };
    }, [cssCustomProp, isDrawerOpen]);
    const formattedAriaLabel = useMemo(() => {
        return ariaLabelButton === null || ariaLabelButton === void 0 ? void 0 : ariaLabelButton.replace('##', activeMarketLabel || 'Global');
    }, [ariaLabelButton, activeMarketLabel]);
    return (React.createElement(Fragment, null,
        React.createElement(Drawer, { id: "market-selector", variant: DrawerVariants.wide, open: isDrawerOpen, position: Alignments.right, onClose: onCloseDrawer, withCloseButton: true, closeButtonProps: {
                ...aria({ label: (drawerProps === null || drawerProps === void 0 ? void 0 : drawerProps.closeDrawerAriaLabel) || 'Close drawer' }),
            }, ...aria({ hidden: !isDrawerOpen }), "aria-labelledby": `${memoizedId}-title`, role: "region" }, isDrawerOpen && (React.createElement(React.Fragment, null,
            React.createElement(SelectRegionTitle, { size: Sizes.medium, level: 1, noWrap: false, lineClamp: 0, accent: false, id: `${memoizedId}-title` }, title || 'Select your region'),
            React.createElement(Spacer, { spacing: { mobile: Spacings.small, tablet: Spacings.xxLarge }, orientation: Orientations.vertical }),
            isMobile && !!regions && (React.createElement(React.Fragment, null,
                React.createElement(Accordion, { largeIcons: true, initialExpanded: {
                        [activeMarketIndex]: true,
                    } }, regions.map(
                // For mobile, we want to add the global markets to the bottom of the list
                (region, index) => (React.createElement(AccordionSection, { key: region.title + index, id: `accordion-region-section-${index}`, title: React.createElement(RegionTitle, { size: Sizes.medium, level: 2, noWrap: true, lineClamp: 0, accent: false }, region.title) }, region.markets.map((market) => (React.createElement(LinkWrapper, { key: market.label + index },
                    React.createElement(MarketLink, { href: market.url, onClick: (e) => onMarketLinkClick(e, market), variant: LinkVariants.navigation, isUnderlinedOnHover: true, isActive: activeMarketLabel === market.label }, market.label)))))))),
                React.createElement(GlobalLinkWrapper, null,
                    React.createElement(MarketLink, { href: global.url, onClick: onGlobalLinkClick, variant: LinkVariants.navigation, isUnderlinedOnHover: true, isActive: activeMarketLabel === (global === null || global === void 0 ? void 0 : global.label) }, global === null || global === void 0 ? void 0 : global.visitGlobalSiteText)))),
            !isMobile && !!regions && (React.createElement(React.Fragment, null,
                React.createElement(AutoGrid, null, gridColumns.map((gridColumn, index) => (React.createElement(AutoGridColumn, { key: `region-column-${index}`, hasDivider: index <= columnsPerRow - 1 },
                    !!gridColumn.title && (React.createElement(RegionTitle, { size: Sizes.medium, level: 2, noWrap: true, lineClamp: 0, accent: false }, gridColumn.title || '&empty;')),
                    React.createElement(MarketsWrapper, { hasTitle: !!gridColumn.title }, gridColumn.markets.map((market, index) => (React.createElement(LinkWrapper, { key: market.label + index },
                        React.createElement(MarketLink, { href: market.url, onClick: (e) => onMarketLinkClick(e, market), variant: LinkVariants.navigation, isUnderlinedOnHover: true, isActive: activeMarketLabel === market.label }, market.label))))))))),
                React.createElement(GlobalLinkWrapper, null,
                    React.createElement(MarketLink, { href: global.url, onClick: onGlobalLinkClick, variant: LinkVariants.navigation, isUnderlinedOnHover: true, isActive: activeMarketLabel === (global === null || global === void 0 ? void 0 : global.label) }, global === null || global === void 0 ? void 0 : global.visitGlobalSiteText))))))),
        open === undefined && (React.createElement(OpenDrawerWrapper, null,
            React.createElement(TextButton, { ref: ref, onClick: onOpenDrawer, "data-type": "market-selector-button", "aria-label": formattedAriaLabel, ...props, tabIndex: isDrawerOpen ? -1 : 0 },
                React.createElement(LabelContainer, null,
                    React.createElement(Icon, { icon: Icons.globe, size: IconSizes.small, pairedWithText: false, style: { marginBlockStart: `-${createCustomSpacing(2 / 16)}` } }),
                    React.createElement(Spacer, { spacing: Spacings.xSmall, orientation: Orientations.horizontal }),
                    activeMarketLabel || `Global`))))));
});
MarketSelector.displayName = 'MarketSelector';
export default MarketSelector;
export var MarketSelectorCSSCustomProps;
(function (MarketSelectorCSSCustomProps) {
    MarketSelectorCSSCustomProps["open"] = "--cw-market-selector-open";
})(MarketSelectorCSSCustomProps || (MarketSelectorCSSCustomProps = {}));
const SelectRegionTitle = styled(Heading)(({ theme }) => css `
    color: ${theme.marketSelector.drawer.titleColor};
  `);
SelectRegionTitle.displayName = `SelectRegionTitle`;
const MarketsWrapper = styled.div(({ hasTitle }) => css(css `
      margin-top: ${Spacings.medium};
    `, !hasTitle &&
    css `
        margin-top: ${Spacings.xxLarge};
      `));
const MarketLink = styled(Link)(({ isActive, theme }) => css(isActive &&
    css `
        color: ${theme.link.variants.navigation.states.active.textColor};
      `));
const RegionTitle = styled(Heading)(({ theme }) => css `
    color: ${theme.marketSelector.drawer.regionTitleColor};
    padding: 0;
    margin: 0;
  `);
const OpenDrawerWrapper = styled.div(({ theme }) => css `
    color: ${theme.button.text.states.enabled.color};
    display: inline-flex;
    margin: 0 auto;
  `);
const AutoGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  ${responsiveStyles({
    gridGap: {
        mobile: `${Spacings.large} ${Spacings.large}`,
        desktop: `${Spacings.large} ${Spacings.xLarge}`,
    },
})}
`;
const AutoGridColumn = styled('div')(({ hasDivider, theme }) => css(css `
      display: flex;
      flex-direction: column;
      padding-block-end: ${Spacings.xxLarge};
    `, hasDivider &&
    css `
        border-bottom: ${createCustomSpacing(1 / 16)} solid
          ${theme.marketSelector.drawer.dividerColor};
      `));
const LinkWrapper = styled.div `
  display: block;
`;
const GlobalLinkWrapper = styled('div')(({ theme }) => css `
    display: flex;
    justify-content: flex-start;
    padding-block-start: ${Spacings.medium};
    border-top: ${createCustomSpacing(1 / 16)} solid ${theme.marketSelector.drawer.dividerColor};
  `);
const LabelContainer = styled.span `
  display: flex;
  align-items: center;
`;
